import { useState, useMemo, useEffect, useCallback, useRef, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';

import { RootState } from '../../../store';
import {
  setRowData,
  setSelectedCounselling,
} from '../../../store/slices/counsellingSlice';
import { alertService } from '../../../shared/services/alertService';
import { exportToExcel, ExcelColumn } from '../../../shared/export/excel';
import {
  useLazyGetNewCounsellingQuery,
  useGetPanelQuery,
  useGetStaffQuery,
  useGetSubjectQuery,
  useLazyGetDisBlockQuery,
  useLazyGetEduDisBlockQuery,
  useLazyGetAllBlocksQuery,
  useLazyGetAllBlockQuery,
  useLazyGetTeacherListQuery,
  useLazyGetTeacherList1Query,
  useLazyGetTeacherList2Query,
  useLazyGetVacancyQuery,
  useLazyGetSelectVacancyQuery,
  useLazyGetTechVacQuery,
  useLazyEditCounsellingQuery,
  useLazyGetTchrPnlDistStsQuery,
  useLazyGetPdfOrderCopyQuery,
  useLazyGetUploadedFilesQuery,
  usePostCounTransferMutation,
  usePostCounTransferLogMutation,
  useAddVacancyNeedLogMutation,
  useTransferVacAddSubMutation,
  useTransferVacAddSub2Mutation,
  useTechTransferVacPostMutation,
  useTechTransferVacPostAidedMutation,
  usePostPlanCounsellingMutation,
} from '../counselling.services';
import {
  CounsellingRecord,
  Option,
  RoleContext,
  CounsellingPath,
} from './types';
import { buildCounsellingList, deriveIsScert } from './counsellingFilters';
import { useBreadcrumb } from '../../../layouts/BreadcrumbContext';
import CounsellingCard from './CounsellingCard';
import { buildColumnSets } from './columns';
import { transformTeacherLists, TeacherListsResult } from './transform';
import {
  decorateVacancies,
  decorateSelectedVacancies,
} from './vacancyTransform';
import {
  TransferContext,
  buildConfirmPayload,
  buildTechTransferReq,
  techTransferEndpoint,
  buildTransferVacAddSubData,
  transferVacAddSubEndpoint,
  buildTransferLog,
  buildVacancyLog,
  buildNotWillAbsPayload,
} from './transferLogic';
import { SUBJECT_DESIGNATIONS } from '../../../shared/services/commonService';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Migrated from Angular `DEEComponent`
 * (src/app/views/pages/counselling/dee).
 *
 * Phases 1-5: list screen, clickStart routing, detail tabs (Teacher List,
 * Vacancies, Confirmation, Not Willing/Relinguish, Absent, Download Order),
 * transfer confirmation chain, PDF order copy, Excel export, seniority reset.
 *
 * Separate navigation-target pages (order-copy-details, vacancy-update,
 * promotion-candidate, seniority-type, counselling-reset, auth/order-copy-pdf)
 * are separate migrations and are only navigated to here.
 */

const num = (v: any): number => Number(v);
const str = (v: any): string =>
  v === null || v === undefined ? '' : String(v);

const formatFromDate = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const resolvePath = (
  pathname: string,
  queryPath: string | null
): CounsellingPath => {
  if (pathname === '/counselling/seniority') return 'seniority';
  if (pathname === '/counselling/candidate') return 'candidate';
  if (pathname === '/counselling/vacancy') return 'vacancy';
  if (pathname === '/counselling/counseling-reset') return 'counseling-reset';
  return (queryPath as CounsellingPath) || '';
};

const emptyLists: TeacherListsResult = {
  teacherList: [],
  absentList: [],
  notWillingList: [],
  finalList: [],
  teacherPriority: null,
  teacherPriorityId: '',
};

const OPTION_LIST: Option[] = [
  { label: 'Need', value: '1' },
  { label: 'Vacancies', value: '2' },
];

const RELINGUISH_LIST: Option[] = [
  { label: 'Temporary', value: '1' },
  { label: 'Permanent', value: '2' },
];

// ── Search box ───────────────────────────────────────────────────────────────
interface SearchBoxProps {
  /** Called with the (debounced) search term. Must be stable (useCallback). */
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

/**
 * Self-contained, memoized search box. Holding the input value in local state
 * here means keystrokes re-render only this small component, not the large DEE
 * parent — and the filter callback fires debounced. This removes the typing lag
 * on the Counselling List page.
 */
const SearchBox = memo(
  ({
    onSearch,
    placeholder = 'Search..',
    debounceMs = 250,
  }: SearchBoxProps) => {
    const [value, setValue] = useState('');
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (timer.current) clearTimeout(timer.current);
      };
    }, []);

    const handleChange = (v: string) => {
      setValue(v);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => onSearch(v), debounceMs);
    };

    return (
      <InputText
        value={value}
        type="text"
        className="form-control"
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
      />
    );
  }
);
SearchBox.displayName = 'SearchBox';

const DEE = () => {
  const s = useSelector((state: RootState) => state.session);
  const handoffRowData = useSelector(
    (state: RootState) => state.counselling.rowData
  );
  const handoffCounselling = useSelector(
    (state: RootState) => state.counselling.selectedCounselling
  );
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { setSubItems, clearSubItems } = useBreadcrumb();

  const role: RoleContext = useMemo(() => {
    const userTypeId = String(s.usertypeid ?? '');
    const userType1 = String(s.emisUsertype1 ?? '');
    return {
      userTypeId,
      userType1,
      districtId: Number(s.userId ?? 0),
      isScert: deriveIsScert(userTypeId, userType1),
    };
  }, [s.usertypeid, s.emisUsertype1, s.userId]);

  const path = useMemo(
    () => resolvePath(location.pathname, searchParams.get('path')),
    [location.pathname, searchParams]
  );

  // ── Static dropdown data ────────────────────────────────────────────────
  const { data: panelRaw } = useGetPanelQuery();
  const { data: staffRaw } = useGetStaffQuery();
  const { data: subjectRaw } = useGetSubjectQuery();

  const PanelList: any[] = useMemo(() => panelRaw?.Panel ?? [], [panelRaw]);
  const DesignationList: any[] = useMemo(
    () => staffRaw?.AllStaffType ?? [],
    [staffRaw]
  );
  const SubList: Option[] = useMemo(
    () => subjectRaw?.AllSubject ?? [],
    [subjectRaw]
  );

  // ── Lazy queries ─────────────────────────────────────────────────────────
  const [triggerNewCounselling] = useLazyGetNewCounsellingQuery();
  const [triggerDisBlock] = useLazyGetDisBlockQuery();
  const [triggerEduDisBlock] = useLazyGetEduDisBlockQuery();
  const [triggerAllBlocks] = useLazyGetAllBlocksQuery();
  const [triggerAllBlock] = useLazyGetAllBlockQuery();
  const [triggerTeacherList] = useLazyGetTeacherListQuery();
  const [triggerTeacherList1] = useLazyGetTeacherList1Query();
  const [triggerTeacherList2] = useLazyGetTeacherList2Query();
  const [triggerVacancy] = useLazyGetVacancyQuery();
  const [triggerSelectVacancy] = useLazyGetSelectVacancyQuery();
  const [triggerTechVac] = useLazyGetTechVacQuery();
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [triggerPnlDist] = useLazyGetTchrPnlDistStsQuery();
  const [triggerPdfOrderCopy] = useLazyGetPdfOrderCopyQuery();
  const [triggerUploadedFiles] = useLazyGetUploadedFilesQuery();

  // ── Mutations ────────────────────────────────────────────────────────────
  const [postCounTransfer] = usePostCounTransferMutation();
  const [postCounTransferLog] = usePostCounTransferLogMutation();
  const [addVacancyNeedLog] = useAddVacancyNeedLogMutation();
  const [transferVacAddSub] = useTransferVacAddSubMutation();
  const [transferVacAddSub2] = useTransferVacAddSub2Mutation();
  const [techTransferVacPost] = useTechTransferVacPostMutation();
  const [techTransferVacPostAided] = useTechTransferVacPostAidedMutation();
  const [postPlanCounselling] = usePostPlanCounsellingMutation();

  // ── List-screen state ──────────────────────────────────────────────────────
  const [counsellingList, setCounsellingList] = useState<CounsellingRecord[]>(
    []
  );
  const [searchStudentList, setSearchStudentList] = useState<
    CounsellingRecord[]
  >([]);
  const [blockList, setBlockList] = useState<Option[]>([]);
  // Bumping this remounts <SearchBox> to clear its input (e.g. on Back).
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // ── Detail-screen state ────────────────────────────────────────────────────
  const [isCounsellingCard, setIsCounsellingCard] = useState(true);
  const [startEndRow, setStartEndRow] = useState<CounsellingRecord | null>(
    null
  );
  const [activeTab, setActiveTab] = useState(0);
  const [tabKeyName, setTabKeyName] = useState('0');
  const [tab1Disabled, setTab1Disabled] = useState(true);
  const [tab2Disabled, setTab2Disabled] = useState(true);
  const [lists, setLists] = useState<TeacherListsResult>(emptyLists);
  const [loading, setLoading] = useState(false);
  // Global search term per detail table.
  const [globalFilter, setGlobalFilter] = useState('');
  const [vacancyFilter, setVacancyFilter] = useState('');
  const [confirmationFilter, setConfirmationFilter] = useState('');
  const [notWillingFilter, setNotWillingFilter] = useState('');
  const [absentFilter, setAbsentFilter] = useState('');
  const [finalFilter, setFinalFilter] = useState('');

  // ── Vacancy / selection state ──────────────────────────────────────────────
  const [selectedTeacher, setSelectedTeacher] = useState<any[]>([]);
  const [clickSelectRow, setClickSelectRow] = useState<any>(null);
  const [allVacancyList, setAllVacancyList] = useState<any[]>([]);
  const [selectedVacancyList, setSelectedVacancyList] = useState<any[]>([]);
  const [vacancyList, setVacancyList] = useState<any[]>([]);
  const [activeIndexId, setActiveIndexId] = useState(0);
  const [techVacDetails, setTechVacDetails] = useState<any>('');
  const [selectedVacancyRow, setSelectedVacancyRow] = useState<any>(null);

  // ── Confirmation / transfer state ───────────────────────────────────────────
  const [confirmationList, setConfirmationList] = useState<any[]>([]);
  const [rowSelect, setRowSelect] = useState<any>(null);
  const [confirmRow, setConfirmRow] = useState<any>(null);
  const [vacNeed, setVacNeed] = useState<any>('');
  const [isVacNeedDisabled, setIsVacNeedDisabled] = useState(false);
  const [banStatus, setBanStatus] = useState<any>(1);
  const [handleConfirmLoading, setHandleConfirmLoading] = useState(false);
  const [moveToTransfer, setMoveToTransfer] = useState(false);
  const [moveToTransferLoading, setMoveToTransferLoading] = useState(false);
  const [finalConfirmApiData, setFinalConfirmApiData] = useState<any>(null);

  // ── Dialog state ─────────────────────────────────────────────────────────
  const [confirmModal, setConfirmModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [notWillAbsModal, setNotWillAbsModal] = useState(false);
  const [resetModel, setResetModel] = useState(false);
  const [relinguishModal, setRelinguishModal] = useState(false);
  const [notWillAbsText, setNotWillAbsText] = useState('');
  const [notWillAbsCont, setNotWillAbsCont] = useState('');
  const [rowObject, setRowObject] = useState<any>(null);
  const [relinguish, setRelinguish] = useState<any>('');
  const [relinguishRow, setRelinguishRow] = useState<any>(null);
  const [resetCoun, setResetCoun] = useState<any>(null);

  // Angular showed a different tab set per path:
  //   candidate -> only Teacher List, vacancy -> only Vacancies,
  //   empty (conduct counselling) -> all 6 tabs.
  const tabOptions = useMemo(() => {
    if (path === 'candidate') {
      return [{ label: 'Teacher List', value: '0' }];
    }
    if (path === 'vacancy') {
      return [{ label: 'Vacancies', value: '1' }];
    }
    return [
      { label: 'Teacher List', value: '0' },
      { label: 'Vacancies', value: '1', disabled: tab1Disabled },
      { label: 'Confirmation', value: '2', disabled: tab2Disabled },
      { label: 'Not Willing / Relinguish', value: '3' },
      { label: 'Absent', value: '4' },
      { label: 'Download Order', value: '5' },
    ];
  }, [path, tab1Disabled, tab2Disabled]);

  const columnSets = useMemo(
    () => buildColumnSets(startEndRow, role),
    [startEndRow, role]
  );

  const isOfc = columnSets.isOfc;

  // ── Top-row action gating (Angular *ngIf on the Action cell) ───────────────
  const showRowActions = useCallback(
    (rowData: any, rowIndex: number): boolean => {
      if (rowIndex !== 0 || !startEndRow) return false;
      const loc = str(startEndRow.Location);
      const { userTypeId: u, userType1: u1, districtId } = role;
      const { teacherPriority, teacherPriorityId } = lists;

      if (loc === '3') {
        if ((u === '5' && u1 === '7') || (u === '5' && u1 === '8')) {
          return teacherPriorityId === rowData.uptId;
        }
        if (u === '10' && u1 === '2') {
          return (
            num(rowData.EduDisId) === districtId &&
            teacherPriorityId === rowData.uptId
          );
        }
        return (
          teacherPriority === districtId &&
          num(rowData.District) === districtId &&
          teacherPriorityId === rowData.uptId
        );
      }
      return teacherPriority === rowData.uptId;
    },
    [startEndRow, role, lists]
  );

  // ── Willing button gating (tab 3, Angular *ngIf) ────────────────────────────
  const showWillingButton = useCallback(
    (rowData: any): boolean => {
      if (!startEndRow) return false;
      if (str(startEndRow.willing_sts) !== '1') return false;
      const loc = str(startEndRow.Location);
      const dir = str(startEndRow.Directorate);
      const { districtId } = role;
      return (
        (loc === '2' && num(rowData.District) === districtId) ||
        ((loc === '4' || loc === '3') &&
          (num(rowData.EduDisId) === districtId ||
            num(rowData.District) === districtId)) ||
        loc === '1' ||
        loc === '5' ||
        (dir === '6' && loc === '3')
      );
    },
    [startEndRow, role]
  );

  // ── Download Order PDF gating (tab 5, Angular *ngIf) ────────────────────────
  const showFinalPdf = useCallback(
    (rowData: any): 'button' | 'dash' | 'plain-button' => {
      const { userTypeId: u, userType1: u1, districtId } = role;
      if (
        (u === '22' && districtId === num(rowData.District)) ||
        (u === '5' && u1 === '7' && str(rowData.school_key_id) === '90633') ||
        (u === '5' && u1 === '6')
      ) {
        return 'button';
      }
      if (
        (u === '22' && districtId !== num(rowData.District)) ||
        (u === '5' && u1 === '7' && str(rowData.school_key_id) !== '90633')
      ) {
        return 'dash';
      }
      // Angular also shows a plain button when !isScert.
      return role.isScert ? 'dash' : 'plain-button';
    },
    [role]
  );

  // ── Block fetch by role ──────────────────────────────────────────────────
  const getBlock = useCallback(async () => {
    const { userTypeId, districtId } = role;
    try {
      let res: any;
      if (userTypeId === '9') {
        res = await triggerDisBlock({ districtId }).unwrap();
      } else if (userTypeId === '10') {
        res = await triggerEduDisBlock({ eduDistrictId: districtId }).unwrap();
      } else if (userTypeId === '5') {
        res = await triggerAllBlocks().unwrap();
      } else {
        res = await triggerAllBlock().unwrap();
      }
      if (res?.dataStatus) {
        setBlockList(
          (res.result ?? []).map((mp: any) => ({
            label: mp.BlkNme,
            value: mp.BlkId,
          }))
        );
      }
    } catch {
      setBlockList([]);
    }
  }, [
    role,
    triggerDisBlock,
    triggerEduDisBlock,
    triggerAllBlocks,
    triggerAllBlock,
  ]);

  // ── Load + build the counselling list ──────────────────────────────────────
  const getAllNewCounselling = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: any = await triggerNewCounselling().unwrap();
      if (!res?.dataStatus) {
        setCounsellingList([]);
        setSearchStudentList([]);
        setIsLoading(false);
        return;
      }
      // Precompute O(1) lookup maps so the per-row split/join is O(n) instead
      // of O(rows × panels × subjects) — this removes the card-loading hang.
      const panelById = new Map<string, any>();
      PanelList.forEach((pt: any) => panelById.set(String(pt.id), pt));
      const subjectByValue = new Map<string, any>();
      (SubList ?? []).forEach((pt) => subjectByValue.set(String(pt.value), pt));

      const mapped: CounsellingRecord[] = (res.result ?? []).map(
        (item: CounsellingRecord) => {
          const row: CounsellingRecord = { ...item };
          if (typeof row.SclPanel === 'string') {
            row.SclPanel = row.SclPanel.split(',').map(
              (pid: string) => panelById.get(String(pid)) ?? []
            );
          }
          if (typeof row.Subject === 'string') {
            row.Subject = row.Subject.split(',').map(
              (sid: string) => subjectByValue.get(String(sid)) ?? []
            );
          }
          row.FromDate = formatFromDate(row.FromDate);
          return row;
        }
      );
      const filtered = buildCounsellingList(mapped, path, role);
      setCounsellingList(filtered);
      setSearchStudentList(filtered);
    } catch {
      setCounsellingList([]);
      setSearchStudentList([]);
    } finally {
      setIsLoading(false);
    }
  }, [triggerNewCounselling, PanelList, SubList, path, role]);

  useEffect(() => {
    if (!panelRaw || !staffRaw || !subjectRaw) return;
    getBlock();
    getAllNewCounselling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelRaw, staffRaw, subjectRaw, path]);

  // ── OrderBack re-entry (Angular: selectRowData?.IsMutual == 'OrderBack') ────
  // Only the order-copy Back flow stores a hand-off (IsMutual === 'OrderBack').
  // In that case, jump straight to the Download Order tab (5). For any other
  // entry into this page, clear the stale hand-off so it doesn't leak in.
  useEffect(() => {
    if (
      handoffRowData?.IsMutual === 'OrderBack' &&
      handoffCounselling &&
      !startEndRow
    ) {
      setStartEndRow(handoffCounselling);
      setIsCounsellingCard(false);
      setActiveTab(5);
      setTabKeyName('5');
      fetchTeacherList2(handoffCounselling);
    } else if (handoffRowData && handoffRowData.IsMutual !== 'OrderBack') {
      dispatch(setRowData(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffRowData, handoffCounselling]);

  // Filter the card list by title. Debounce + input value live in <SearchBox>,
  // so this callback only runs when the user pauses typing.
  const onSearch = useCallback(
    (value: string) => {
      const term = value.trim().toLowerCase();
      if (term) {
        setCounsellingList(
          searchStudentList.filter(
            (item) =>
              item &&
              item.Title &&
              String(item.Title).toLowerCase().indexOf(term) > -1
          )
        );
      } else {
        setCounsellingList(searchStudentList);
      }
    },
    [searchStudentList]
  );

  // Update a field on a single card in both the visible and backup lists.
  // The backup is matched by object REFERENCE (identity) rather than IndexId,
  // because the API can return duplicate IndexIds — a reference match keeps the
  // edit scoped to exactly one card and preserves it when the search clears.
  const patchCounsellingRow = useCallback(
    (index: number, field: string, value: any) => {
      setCounsellingList((prev) => {
        const target = prev[index];
        const updated = target ? { ...target, [field]: value } : target;
        const next = prev.map((row, i) => (i === index ? updated : row));
        setSearchStudentList((backup) =>
          backup.map((row) => (row === target ? updated : row))
        );
        return next;
      });
    },
    []
  );

  const handlePanelType = useCallback(
    (value: any, index: number) =>
      patchCounsellingRow(index, 'PanelType', value),
    [patchCounsellingRow]
  );
  const handleSubject = useCallback(
    (value: any, index: number) =>
      patchCounsellingRow(index, 'SubjectId', value),
    [patchCounsellingRow]
  );
  const handleBlock = useCallback(
    (value: any, index: number) => patchCounsellingRow(index, 'BlockId', value),
    [patchCounsellingRow]
  );

  // ── Teacher lists ────────────────────────────────────────────────────────
  const fetchTeacherList = useCallback(
    async (row: CounsellingRecord, useApprovedVariant: boolean) => {
      setLoading(true);
      const records = {
        counsellingId: row.IndexId,
        panelType: row.PanelType?.id,
        subject: row.SubjectId ? row.SubjectId : '',
        location: row.Location,
        blockId: row.BlockId ? row.BlockId : '',
        managementId: row.School ? row.School : '',
      };
      try {
        const trigger = useApprovedVariant
          ? triggerTeacherList1
          : triggerTeacherList;
        const res: any = await trigger(records).unwrap();
        if (res?.dataStatus && (res.result ?? []).length > 0) {
          setLists(
            transformTeacherLists({
              result: res.result,
              startEndRow: row,
              role,
              designationList: DesignationList,
              subList: SubList,
              path,
              requireApproved: useApprovedVariant,
            })
          );
        } else {
          setLists(emptyLists);
        }
      } catch {
        setLists(emptyLists);
      } finally {
        setLoading(false);
      }
    },
    [
      triggerTeacherList,
      triggerTeacherList1,
      role,
      DesignationList,
      SubList,
      path,
    ]
  );

  const fetchTeacherList2 = useCallback(
    async (row: CounsellingRecord) => {
      setLoading(true);
      const records = {
        counsellingId: row.IndexId,
        panelType: row.PanelType?.id,
        subject: row.SubjectId ? row.SubjectId : '',
        location: row.Location,
        blockId: row.BlockId ? row.BlockId : '',
        managementId: row.School ? row.School : '',
      };
      try {
        const res: any = await triggerTeacherList2(records).unwrap();
        if (res?.dataStatus && (res.result ?? []).length > 0) {
          const built = transformTeacherLists({
            result: res.result,
            startEndRow: row,
            role,
            designationList: DesignationList,
            subList: SubList,
            path,
            requireApproved: false,
          });
          // list2 refreshes absent / notWilling / final (Angular).
          built.finalList.sort((a, b) =>
            num(a.seniority_state) > num(b.seniority_state) ? -1 : 1
          );
          // Angular GetAllTeacherList2 also refreshes the candidate teacher list.
          setLists((prev) => ({
            ...prev,
            absentList: built.absentList,
            notWillingList: built.notWillingList,
            finalList: built.finalList,
            teacherList:
              path === 'candidate' ? built.teacherList : prev.teacherList,
          }));
        } else {
          setLists((prev) => ({
            ...prev,
            absentList: [],
            notWillingList: [],
            finalList: [],
          }));
        }
      } catch {
        // keep prior lists
      } finally {
        setLoading(false);
      }
    },
    [triggerTeacherList2, role, DesignationList, SubList, path]
  );

  // ── clickStart ─────────────────────────────────────────────────────────────
  const handleStart = useCallback(
    (row: CounsellingRecord) => {
      const subj = row.SubjectId
        ? row.SubjectId
        : row.Subject && row.Subject[0]?.value;
      const pan = row.PanelType && row.PanelType.id;
      // Hand-off to the order-copy / vacancy pages via router state (keeps the
      // URL clean — no query params). Target pages read useLocation().state.
      const navState = {
        state: {
          path: row.IndexId,
          panelType: String(pan ?? ''),
          subject: String(subj ?? ''),
          canUpdtBy: String(row.CanUpdtBy ?? ''),
          manageId: String(row.School ?? ''),
          block: String(row.BlockId ?? ''),
        },
      };
      if (path === 'order-copy') {
        navigate('/counselling/order-copy-details', navState);
        return;
      }
      if (path === 'update-vac') {
        navigate('/counselling/vacancy-update', navState);
        return;
      }
      if (path === 'add-candidate') {
        navigate('/counselling/promotion-candidate', navState);
        return;
      }
      if (path === 'seniority') {
        navigate('/counselling/seniority-type', navState);
        return;
      }
      if (path === 'counseling-reset') {
        navigate('/counselling/counselling-reset', navState);
        return;
      }

      setStartEndRow(row);
      setIsCounsellingCard(false);
      setTab1Disabled(true);
      setTab2Disabled(true);
      dispatch(setSelectedCounselling(row));

      if (path === 'candidate') {
        setActiveTab(0);
        setTabKeyName('0');
        fetchTeacherList(row, false);
      } else if (path === 'vacancy') {
        setActiveTab(1);
        setTabKeyName('1');
        // Select the single "Vacancies List" sub-tab up-front (index 0), so it
        // is active immediately on entry — before the data finishes loading.
        setActiveIndexId(0);
        getVacancyForPath(row);
      } else {
        setActiveTab(0);
        setTabKeyName('0');
        fetchTeacherList(row, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, navigate, dispatch, fetchTeacherList]
  );

  // ── Vacancy fetching (getTechVac / getAllVacancies / getSelectedVacancies) ──
  const getTechVac = useCallback(
    async (selRow: any, se: CounsellingRecord) => {
      const schId = selRow.school_key_id;
      const panel = se?.PanelType?.id;
      const des = selRow?.desgnation;
      const sub = role.isScert
        ? selRow.subject
        : str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
          ? selRow.Post
            ? selRow.Post
            : se.SubjectId
          : str(selRow.desgnation) === '103'
            ? selRow.subject
            : str(selRow.desgnation) === '117'
              ? '99'
              : se.SubjectId;
      const aided = se.schType;
      try {
        const res: any = await triggerTechVac({
          schoolId: schId,
          panel,
          designation: des,
          subject: sub,
          aided,
        }).unwrap();
        setTechVacDetails(
          res?.dataStatus && res.result.length > 0 ? res.result[0] : ''
        );
      } catch {
        setTechVacDetails('');
      }
    },
    [role.isScert, triggerTechVac]
  );

  const getAllVacancies = useCallback(
    async (selRow: any, se: CounsellingRecord) => {
      const gen = selRow.gender === 'Male' ? '1' : '2';
      const sub = role.isScert
        ? selRow.subject
        : str(se.SubjectId) === '26' || str(se.SubjectId) === '27'
          ? selRow.subject
          : str(se.Designation) === '103'
            ? selRow.subject
            : se.SubjectId;
      const blockId =
        str(se.CounsellingType) === '7' ? selRow.HomeBlkId : se.BlockId;
      const mangeId =
        str(se.CounsellingType) === '7' ? selRow.HomeMngId : se.School;
      const minority = selRow.MinorityType ? selRow.MinorityType : '';
      try {
        const res: any = await triggerVacancy({
          counsellingId: selRow.counselling_id,
          gender: gen,
          subject: sub,
          panelId: se?.PanelType?.id,
          blockId,
          managementId: mangeId,
          minority,
        }).unwrap();
        if (res?.dataStatus) {
          let vac = decorateVacancies(res.result, SubList, DesignationList);
          if (
            (role.userTypeId === '22' ||
              (role.userTypeId === '5' && role.userType1 === '7')) &&
            str(se.CounsellingType) === '2'
          ) {
            vac = vac.filter(
              (fr: any) => fr.school_id !== selRow.school_key_id
            );
          }
          setAllVacancyList(vac);
        } else {
          setAllVacancyList([]);
        }
      } catch {
        setAllVacancyList([]);
      }
    },
    [role, SubList, DesignationList, triggerVacancy]
  );
  // Angular clickStart 'vacancy' path: getVacancy(IndexId,'',subj,pan,Block,School,'')
  const getVacancyForPath = useCallback(
    async (se: CounsellingRecord) => {
      let subj = se.SubjectId
        ? se.SubjectId
        : se.Subject && se.Subject[0]?.value;
      const pan = se.PanelType && se.PanelType.id;
      if (str(se.Designation) === '103') {
        subj = '100';
      }
      try {
        const res: any = await triggerVacancy({
          counsellingId: se.IndexId,
          gender: '',
          subject: subj,
          panelId: pan,
          blockId: se.BlockId,
          managementId: se.School,
          minority: '',
        }).unwrap();
        if (res?.dataStatus) {
          const vac = decorateVacancies(res.result, SubList, DesignationList);
          setAllVacancyList(vac);
          // For the vacancy path the TabView renders only the "Vacancies List"
          // panel, so it lives at index 0 (the pre-selected panel is omitted).
          setActiveIndexId(0);
          setVacancyList(vac);
        } else {
          setAllVacancyList([]);
          setVacancyList([]);
        }
      } catch {
        setAllVacancyList([]);
        setVacancyList([]);
      }
    },
    [triggerVacancy, SubList, DesignationList]
  );

  const getSelectedVacancies = useCallback(
    async (selRow: any, se: CounsellingRecord) => {
      const sbjt = role.isScert ? selRow.subject : se.SubjectId;
      const req = {
        counsellingId: selRow.counselling_id,
        panelId: se?.PanelType?.id,
        teacherId: selRow?.teacher_id,
        subject: sbjt,
        blockId:
          str(se.CounsellingType) === '7' ? selRow.HomeBlkId : se.BlockId,
        managementId:
          str(se.CounsellingType) === '7' ? selRow.HomeMngId : se.School,
      };
      try {
        const res: any = await triggerSelectVacancy(req).unwrap();
        if (res?.dataStatus) {
          const dec = decorateSelectedVacancies(
            res.result,
            SubList,
            DesignationList
          );
          setSelectedVacancyList(dec);
          setActiveIndexId(0);
          setVacancyList(dec);
        } else {
          setSelectedVacancyList([]);
        }
      } catch {
        setSelectedVacancyList([]);
      }
    },
    [role.isScert, SubList, DesignationList, triggerSelectVacancy]
  );

  const clickSelectBtn = useCallback(
    (row: any) => {
      if (!startEndRow) return;
      setClickSelectRow(row);
      setActiveTab(1);
      setTabKeyName('1');
      setTab1Disabled(false);
      setNotWillAbsText('Select');
      setActiveIndexId(0);
      setVacancyList([]);
      if (str(startEndRow.CounsellingType) !== '4') {
        getTechVac(row, startEndRow);
        getSelectedVacancies(row, startEndRow);
      }
      getAllVacancies(row, startEndRow);
      setSelectedTeacher([row]);
    },
    [startEndRow, getTechVac, getSelectedVacancies, getAllVacancies]
  );

  // ── handleTabView (pre-selected vs all vacancies sub-tabs) ─────────────────
  const handleTabView = useCallback(
    (index: number) => {
      setActiveIndexId(index);
      // For the vacancy path there is no "Pre Selected" panel, so the single
      // tab (index 0) always shows all vacancies. For the conduct path,
      // index 0 = pre-selected, index 1 = all.
      const list =
        path === 'vacancy'
          ? allVacancyList
          : index === 0
            ? selectedVacancyList
            : allVacancyList;
      setVacancyList(list);
    },
    [path, selectedVacancyList, allVacancyList]
  );

  const totalVacncy = useMemo(
    () =>
      vacancyList.reduce((acc: number, cur: any) => num(acc) + num(cur.vac), 0),
    [vacancyList]
  );

  // ── onRowSelect (build confirmation record) ─────────────────────────────────
  const onRowSelect = useCallback(
    (e: any) => {
      if (path !== '' || !clickSelectRow) return;
      const rowData = e.data;
      setRowSelect({ data: rowData });
      setActiveTab(2);
      setTabKeyName('2');
      setTab2Disabled(false);
      setConfirmationList([
        {
          name: clickSelectRow.name,
          old_udise_code: clickSelectRow.udise_code,
          new_udise_code: rowData.udise_code,
          old_sch_name: role.isScert
            ? clickSelectRow.ofcNme
            : clickSelectRow.school_name || clickSelectRow.ofcNme,
          new_sch_name: rowData.school_name,
          Designation: clickSelectRow.desgnation,
          NewDesignation: clickSelectRow.desgnation,
          counselling_id: clickSelectRow.counselling_id,
          seniority_state: clickSelectRow.seniority_state,
          Subject: clickSelectRow.subject,
          NewSubject: clickSelectRow.NewSubject,
          IndexId: clickSelectRow.IndexId,
          teacher_id: clickSelectRow.teacher_id,
          SchoolKeyId: clickSelectRow.school_key_id,
          NewSchoolKeyId: rowData.school_id,
          vac: rowData.vac,
          need: rowData.need,
          ResultVacYn: clickSelectRow.ResultVacYn,
          uptId: clickSelectRow.uptId,
        },
      ]);
    },
    [path, clickSelectRow, role.isScert]
  );

  const clickConfirmBtn = useCallback((row: any) => {
    setNotWillAbsText('Confirmation');
    setConfirmRow(row);
    setConfirmModal(true);
    setBanStatus(1);
    const vc = num(row.vac);
    const nd = num(row.need);
    const vn = vc === 0 ? '1' : nd === 0 ? '2' : '';
    setVacNeed(vn);
    setIsVacNeedDisabled(!!vn);
    setHandleConfirmLoading(false);
    setMoveToTransfer(false);
    setMoveToTransferLoading(false);
  }, []);

  const clickRejectBtn = useCallback(() => setRejectModal(true), []);
  const handleReject = useCallback(() => {
    setRejectModal(false);
    setActiveTab(1);
    setTabKeyName('1');
  }, []);
  const handleCancel = useCallback(() => {
    setConfirmModal(false);
    setRejectModal(false);
    setNotWillAbsModal(false);
    setResetModel(false);
  }, []);

  // ── Transfer chain (build ctx once per confirm) ─────────────────────────────
  const buildCtx = useCallback((): TransferContext | null => {
    if (!startEndRow) return null;
    return {
      startEndRow,
      clickSelectRow,
      rowSelectData: rowSelect?.data,
      confirmRow,
      techVacDetails,
      vacNeed,
      isScert: role.isScert,
      userName: s.username,
      relinguish,
    };
  }, [
    startEndRow,
    clickSelectRow,
    rowSelect,
    confirmRow,
    techVacDetails,
    vacNeed,
    role.isScert,
    s.username,
    relinguish,
  ]);

  const postTransferLog = useCallback(
    async (ctx: TransferContext) => {
      try {
        await postCounTransferLog(
          buildTransferLog(ctx, rowObject, notWillAbsText)
        ).unwrap();
      } catch {
        /* log-only */
      }
    },
    [postCounTransferLog, rowObject, notWillAbsText]
  );

  const vacancyLog = useCallback(
    async (ctx: TransferContext) => {
      try {
        await addVacancyNeedLog(buildVacancyLog(ctx, rowObject)).unwrap();
      } catch {
        /* log-only */
      }
    },
    [addVacancyNeedLog, rowObject]
  );

  const getCounsellingTransfer = useCallback(
    async (payload: any, ctx: TransferContext) => {
      try {
        const res: any = await postCounTransfer(payload).unwrap();
        if (!res?.dataStatus) {
          alertService.error('Something went wrong, Please try again!');
          return;
        }
        if (
          activeTab === 3 ||
          notWillAbsText === 'Relinguish' ||
          notWillAbsText === 'Not Willing' ||
          notWillAbsText === 'Absent' ||
          notWillAbsText === 'Willing'
        ) {
          setHandleConfirmLoading(false);
          if (startEndRow) {
            fetchTeacherList2(startEndRow);
            fetchTeacherList(startEndRow, true);
          }
        }
        if (
          notWillAbsText === 'Relinguish' ||
          notWillAbsText === 'Not Willing' ||
          notWillAbsText === 'Willing'
        ) {
          alertService.success('Successfully moved');
          setRelinguishModal(false);
        } else if (notWillAbsText === 'Absent') {
          alertService.success('Successfully moved to absent');
        } else if (notWillAbsText === 'Select') {
          setActiveTab(1);
          setTabKeyName('1');
        } else if (notWillAbsText === 'Confirmation') {
          setMoveToTransfer(true);
          setMoveToTransferLoading(false);
          vacancyLog(ctx);
        }
        setNotWillAbsModal(false);
        postTransferLog(ctx);
      } catch {
        alertService.error('Something went wrong, Please try again!');
      }
    },
    [
      postCounTransfer,
      activeTab,
      notWillAbsText,
      startEndRow,
      fetchTeacherList2,
      fetchTeacherList,
      vacancyLog,
      postTransferLog,
    ]
  );

  const transferVacAddSubFunc = useCallback(
    async (ctx: TransferContext, confirmPayload: any) => {
      const data = buildTransferVacAddSubData(ctx, confirmPayload);
      const useV2 = transferVacAddSubEndpoint(ctx.startEndRow) === 'v2';
      try {
        const res: any = useV2
          ? await transferVacAddSub2(data).unwrap()
          : await transferVacAddSub(data).unwrap();
        if (res?.dataStatus) {
          setMoveToTransfer(true);
          getCounsellingTransfer(confirmPayload, ctx);
        } else {
          alertService.error(res.message);
        }
      } catch {
        alertService.error('Something went wrong, Please try again!');
      }
    },
    [transferVacAddSub, transferVacAddSub2, getCounsellingTransfer]
  );

  const techTransferVacInsert = useCallback(
    async (ctx: TransferContext, confirmPayload: any) => {
      const req = buildTechTransferReq(ctx);
      const aided = techTransferEndpoint(ctx.startEndRow) === 'aided';
      try {
        const res: any = aided
          ? await techTransferVacPostAided(req).unwrap()
          : await techTransferVacPost(req).unwrap();
        if (res?.dataStatus) {
          setHandleConfirmLoading(false);
          setMoveToTransfer(true);
          setMoveToTransferLoading(false);
          getCounsellingTransfer(confirmPayload, ctx);
        }
      } catch {
        alertService.error('Something went wrong, Please try again!');
      }
    },
    [techTransferVacPost, techTransferVacPostAided, getCounsellingTransfer]
  );

  const getPanelDistrict = useCallback(
    async (ctx: TransferContext, confirmPayload: any) => {
      const se = ctx.startEndRow;
      const userId =
        str(se.Location) === '1'
          ? selectedTeacher[0]?.Block
          : selectedTeacher[0]?.District;
      try {
        const res: any = await triggerPnlDist({
          location: se.Location,
          userId,
        }).unwrap();
        if (res?.dataStatus) {
          const sts = res.result[0].Sts;
          setBanStatus(sts);
          if (str(sts) === '1') {
            if (str(se.CounsellingType) === '1') {
              transferVacAddSubFunc(ctx, confirmPayload);
            } else {
              techTransferVacInsert(ctx, confirmPayload);
            }
          }
        } else {
          setHandleConfirmLoading(false);
          alertService.error(res.message);
        }
      } catch {
        setHandleConfirmLoading(false);
        alertService.error('Something went wrong, Please try again!');
      }
    },
    [
      selectedTeacher,
      triggerPnlDist,
      transferVacAddSubFunc,
      techTransferVacInsert,
    ]
  );

  const getOneCounselling = useCallback(
    async (ctx: TransferContext, confirmPayload: any) => {
      try {
        const res: any = await triggerEditCounselling({
          counsellingId: ctx.startEndRow.IndexId,
        }).unwrap();
        if (res?.dataStatus) {
          if (res.result.length > 0) {
            if (str(res.result[0].Status) === '1') {
              getPanelDistrict(ctx, confirmPayload);
            } else if (str(res.result[0].Status) === '2') {
              setHandleConfirmLoading(false);
              alertService.error('Timeout for your counselling');
              setIsCounsellingCard(true);
            }
          }
        } else {
          alertService.error(res.message);
        }
      } catch {
        alertService.error('Something went wrong, Please try again!');
      }
    },
    [triggerEditCounselling, getPanelDistrict]
  );

  const handleConfirm = useCallback(() => {
    if (!startEndRow) return;
    const type = str(startEndRow.CounsellingType);
    const ok =
      (type === '1' && vacNeed) ||
      type === '2' ||
      type === '3' ||
      type === '4' ||
      type === '5' ||
      type === '6' ||
      type === '7' ||
      type === '9';
    if (!ok) {
      setHandleConfirmLoading(false);
      alertService.error('Please select vacancy / need');
      return;
    }
    setHandleConfirmLoading(true);
    const ctx = buildCtx();
    if (!ctx) return;
    const payload = buildConfirmPayload(ctx);
    setFinalConfirmApiData(payload);
    getOneCounselling(ctx, payload);
  }, [startEndRow, vacNeed, buildCtx, getOneCounselling]);

  const goToTeacherList = useCallback(async () => {
    if (!finalConfirmApiData) return;
    setMoveToTransferLoading(true);
    const ctx = buildCtx();
    try {
      const res: any = await postCounTransfer(finalConfirmApiData).unwrap();
      if (res?.dataStatus) {
        if (notWillAbsText === 'Confirmation') {
          setConfirmModal(false);
          setRelinguishModal(false);
          setActiveTab(0);
          setTabKeyName('0');
          setTab1Disabled(true);
          setTab2Disabled(true);
          setMoveToTransferLoading(false);
          if (startEndRow) fetchTeacherList(startEndRow, true);
        }
        setNotWillAbsModal(false);
        if (str(res.status) !== '409' && ctx) {
          postTransferLog(ctx);
          vacancyLog(ctx);
        }
      } else if (str(res.status) === '409' && startEndRow) {
        fetchTeacherList(startEndRow, true);
      }
    } catch {
      alertService.error('Something went wrong, Please try again!');
    }
  }, [
    finalConfirmApiData,
    buildCtx,
    postCounTransfer,
    notWillAbsText,
    startEndRow,
    fetchTeacherList,
    postTransferLog,
    vacancyLog,
  ]);

  // ── Not Willing / Absent / Relinguish / Willing ────────────────────────────
  const clickNotWillingBtn = useCallback((row: any) => {
    setNotWillAbsModal(true);
    setNotWillAbsCont('Are you sure you want to move not willing ?');
    setNotWillAbsText('Not Willing');
    setRowObject(row);
  }, []);
  const notWillRevert = useCallback((row: any) => {
    setNotWillAbsModal(true);
    setNotWillAbsCont('Are you sure you want to willing ?');
    setNotWillAbsText('Willing');
    setRowObject(row);
  }, []);
  const clickAbsentBtn = useCallback((row: any) => {
    setNotWillAbsModal(true);
    setNotWillAbsCont('Are you sure you want to move to absent ?');
    setNotWillAbsText('Absent');
    setRowObject(row);
  }, []);
  const clickRelinguish = useCallback((row: any) => {
    setRelinguishModal(true);
    setRelinguishRow(row);
    setRelinguish('');
  }, []);
  const clickConfirmRelinguish = useCallback((row: any) => {
    setNotWillAbsModal(true);
    setNotWillAbsCont('Are you sure you want to move relinguish ?');
    setNotWillAbsText('Relinguish');
    setRowObject(row);
  }, []);
  const handleRelinguishSubmit = useCallback(() => {
    if (relinguish) {
      clickConfirmRelinguish(relinguishRow);
    } else {
      alertService.error('Please select relinguish');
    }
  }, [relinguish, relinguishRow, clickConfirmRelinguish]);

  const handleNotWillAbs = useCallback(() => {
    const ctx = buildCtx();
    if (!ctx) return;
    const payload = buildNotWillAbsPayload(
      rowObject,
      notWillAbsText,
      relinguish
    );
    getCounsellingTransfer(payload, ctx);
  }, [buildCtx, rowObject, notWillAbsText, relinguish, getCounsellingTransfer]);

  // ── Download order PDF ──────────────────────────────────────────────────────
  const setPdfLoading = useCallback((type: string, i: number, bln: boolean) => {
    setLists((prev) => {
      const key = type === '2' ? 'notWillingList' : 'finalList';
      const arr = [...(prev as any)[key]];
      if (arr[i]) arr[i] = { ...arr[i], pdfLoading: bln };
      return { ...prev, [key]: arr };
    });
  }, []);

  const onRowPDF = useCallback(
    async (rowData: any, type: any, i: number) => {
      if (!startEndRow) return;
      setPdfLoading(String(type), i, true);
      rowData.IsMutual = 'OrderBack';
      dispatch(setRowData(rowData));
      dispatch(setSelectedCounselling(startEndRow));
      localStorage.setItem('type', String(type));
      const se = startEndRow;
      const data = {
        counsellingId: se.IndexId,
        rcFlag:
          role.districtId === 16 &&
          role.userTypeId === '10' &&
          role.userType1 === '2' &&
          str(se.Location) === '1'
            ? '2'
            : role.userTypeId === '9' &&
                role.userType1 === '29' &&
                str(se.CounsellingType) === '9'
              ? '3'
              : str(se.schType) === '2' &&
                  str(se.Location) === '4' &&
                  role.userTypeId === '10' &&
                  role.userType1 === '3'
                ? '2'
                : se.Location,
        type,
        teacherId:
          str(se.CounsellingType) === '4'
            ? `'${rowData.application_id}'`
            : rowData.teacher_id,
        teacherStatus: rowData.teacher_status,
        subject: SUBJECT_DESIGNATIONS.includes(str(se.Designation))
          ? rowData.subject
          : 'null',
        counsellingType: se?.CounsellingType,
      };
      try {
        const res: any = await triggerPdfOrderCopy(data).unwrap();
        setPdfLoading(String(type), i, false);
        if (res?.dataStatus && res.result[0].RCNum) {
          navigate('/auth/order-copy-pdf');
        } else {
          alertService.error('Please update Rc Number');
        }
      } catch {
        setPdfLoading(String(type), i, false);
        alertService.error('Please update Rc Number');
      }
    },
    [startEndRow, role, dispatch, triggerPdfOrderCopy, navigate, setPdfLoading]
  );

  // ── Uploaded order-copy file open — Angular getUploadedFiles ───────────────
  // Builds OC_{IndexId}_{teacherId}.pdf and opens the signed URL in a new tab.
  // (Angular defines this; its call inside onRowPDF is commented out, so it is
  // kept here for parity but not wired to a button. Prefixed with _ to satisfy
  // the unused-vars rule.)
  const _getUploadedFiles = useCallback(
    async (filename: string) => {
      if (!startEndRow) return;
      const file = `OC_${startEndRow.IndexId}_${filename}.pdf`;
      try {
        const result: any = await triggerUploadedFiles({
          bucketName: 'renewalapplicationemis',
          fileName: file,
          expiry: 1800,
        }).unwrap();
        if (result?.url) {
          window.open(result.url, '_blank');
        }
      } catch {
        alertService.error('Unable to open file');
      }
    },
    [startEndRow, triggerUploadedFiles]
  );

  // ── Priority document (candidate list) — Angular getPriorityFile ───────────
  const getPriorityFile = useCallback(
    async (filename: string) => {
      try {
        const result: any = await triggerUploadedFiles({
          bucketName: 'renewalapplicationemis',
          fileName: filename,
          expiry: 1800,
        }).unwrap();
        if (result?.url) {
          window.open(result.url, '_blank');
        }
      } catch {
        alertService.error('Unable to open file');
      }
    },
    [triggerUploadedFiles]
  );

  // ── Excel export ────────────────────────────────────────────────────────────
  const exportExcel = useCallback(() => {
    if (path === 'candidate') {
      const rows = lists.teacherList.map((t: any) => {
        const desi = DesignationList.filter(
          (ct) => str(ct.value) === str(t.desgnation)
        );
        return {
          application_id: t.application_id,
          teacher_id: t.teacher_id,
          name: t.name,
          desgnation: desi.length > 0 ? desi[0].label : '',
          subject: t.subjectLabel,
          udise_code: t.udise_code,
          school_name: t.school_name,
          DistrictName: t.DistrictName,
          EducationDistrictName: t.eduDisName,
          BlockName: t.blkName,
          ofcNme: t.ofcNme,
          priority: t.priority,
          dob: t.dob,
          date_appointment: t.dateAppointmt,
          DojPresentSchool: t.dojPrSch,
          DojPresentPost: t.dojPrsnt,
          DojDept: t.dojDept,
          gender: t.gender,
          seniority_state: t.seniority_state,
          HmAprvSts: t.HmAprvSts,
        };
      });

      // Angular: isScert → drop school_name + udise_code; else → drop ofcNme.
      const baseColumns: ExcelColumn[] = [
        { key: 'application_id', label: 'Application Id' },
        { key: 'teacher_id', label: 'Teacher Id' },
        { key: 'name', label: 'Name' },
        { key: 'desgnation', label: 'Designation' },
        { key: 'subject', label: 'Subject' },
        { key: 'udise_code', label: 'UDISE Code' },
        { key: 'school_name', label: 'School Name' },
        { key: 'DistrictName', label: 'District Name' },
        { key: 'EducationDistrictName', label: 'Education District Name' },
        { key: 'BlockName', label: 'Block Name' },
        { key: 'ofcNme', label: 'Office Name' },
        { key: 'priority', label: 'Priority' },
        { key: 'dob', label: 'DOB' },
        { key: 'date_appointment', label: 'Date of Appointment' },
        { key: 'DojPresentSchool', label: 'DOJ Present School' },
        { key: 'DojPresentPost', label: 'DOJ Present Post' },
        { key: 'DojDept', label: 'DOJ Department' },
        { key: 'gender', label: 'Gender' },
        { key: 'seniority_state', label: 'Seniority No.' },
        { key: 'HmAprvSts', label: 'Approval Status' },
      ];
      const removeKeys: string[] = role.isScert
        ? ['school_name', 'udise_code']
        : ['ofcNme'];
      const columns = baseColumns.filter(
        (c) => !removeKeys.includes(c.key as string)
      );

      exportToExcel({
        data: rows as Record<string, unknown>[],
        columns,
        fileName: 'Teacher List',
      });
    } else {
      const desi = DesignationList.filter(
        (ct) => str(ct.value) === str(startEndRow?.Designation)
      );
      const rows = vacancyList.map((v: any) => ({
        udise_code: v.udise_code,
        school_type: v.school_type,
        type: v.type,
        district_name: v.district_name,
        edu_dist_name: v.edu_dist_name,
        block2: v.block2,
        school_name: v.school_name,
        vac: v.vac,
        need: v.need,
        management: v.management,
        subjectLabel: v.subjectLabel,
        designation: desi.length > 0 ? desi[0].label : startEndRow?.Designation,
      }));
      exportToExcel({
        data: rows,
        columns: [
          { key: 'udise_code', label: 'Udise Code' },
          { key: 'school_type', label: 'School Type' },
          { key: 'type', label: 'Type' },
          { key: 'district_name', label: 'District Name' },
          { key: 'edu_dist_name', label: 'Education District Name' },
          { key: 'block2', label: 'Block' },
          { key: 'school_name', label: 'School Name' },
          { key: 'vac', label: 'Vac' },
          { key: 'need', label: 'Need' },
          { key: 'management', label: 'Management' },
          { key: 'subjectLabel', label: 'Subject Label' },
          { key: 'designation', label: 'Designation' },
        ],
        fileName: 'Vacancy List',
      });
    }
  }, [
    path,
    lists.teacherList,
    vacancyList,
    DesignationList,
    startEndRow,
    role.isScert,
  ]);

  // ── Seniority reset ──────────────────────────────────────────────────────
  const clickReset = useCallback((coun: any) => {
    setResetCoun(coun);
    setResetModel(true);
  }, []);
  const handleReset = useCallback(async () => {
    if (!resetCoun) return;
    try {
      const res: any = await postPlanCounselling({
        records: {
          IndexId: resetCoun.IndexId,
          SeniorityReset: '0',
          SeniorityOrder: '0',
        },
      }).unwrap();
      if (res?.dataStatus) {
        alertService.success('Seniority order reset successfully');
        getAllNewCounselling();
        setResetModel(false);
      }
    } catch {
      alertService.error('Something went wrong, Please try again!');
    }
  }, [resetCoun, postPlanCounselling, getAllNewCounselling]);

  // ── Detail tab / nav handlers ────────────────────────────────────────────
  const handleTabClick = useCallback(
    (value: string) => {
      if (path === 'vacancy') return;
      const index = Number(value);
      setActiveTab(index);
      setTabKeyName(value);
      if (!startEndRow) return;
      if (index === 3 || index === 4 || index === 5) {
        fetchTeacherList2(startEndRow);
      } else {
        fetchTeacherList(startEndRow, true);
      }
    },
    [path, startEndRow, fetchTeacherList2, fetchTeacherList]
  );

  const handleRefresh = useCallback(() => {
    if (!startEndRow) return;
    if (path === 'candidate') fetchTeacherList(startEndRow, false);
    else fetchTeacherList(startEndRow, true);
  }, [startEndRow, path, fetchTeacherList]);

  const handleBack = useCallback(() => {
    if (!path && activeTab > 0) {
      const prev = activeTab - 1;
      setActiveTab(prev);
      setTabKeyName(String(prev));
      return;
    }
    dispatch(setRowData(''));
    setIsCounsellingCard(true);
    setStartEndRow(null);
    setSearchResetKey((k) => k + 1);
    getAllNewCounselling();
  }, [path, activeTab, dispatch, getAllNewCounselling]);

  const handleCounsellingURL = useCallback(
    () => setIsCounsellingCard(true),
    []
  );
  const handleCandidateURL = useCallback(() => {
    dispatch(setRowData(''));
    navigate('/counselling/front-page');
  }, [dispatch, navigate]);

  // Feed the app-shell breadcrumb's last crumb: "Counselling List" on the card
  // screen, or the selected counselling's Title on the detail screen. Clicking
  // it returns to the list (handleCounsellingURL).
  useEffect(() => {
    // Only add the last crumb on the detail screen (the selected counselling's
    // Title). On the card list, no extra crumb — the page title is the last one,
    // so the breadcrumb doesn't show a dangling trailing separator.
    if (!isCounsellingCard && startEndRow?.Title) {
      setSubItems([
        { label: startEndRow.Title, onClick: handleCounsellingURL },
      ]);
    } else {
      clearSubItems();
    }
    return () => clearSubItems();
  }, [
    isCounsellingCard,
    startEndRow,
    handleCounsellingURL,
    setSubItems,
    clearSubItems,
  ]);

  // ── Start/Select button disabled logic (Angular [disabled] expression) ─────
  const isStartDisabled = useCallback(
    (counList: CounsellingRecord): boolean => {
      const desig = str(counList.Designation);
      const loc = str(counList.Location);
      const cType = str(counList.CounsellingType);
      const p = String(path);
      const { userTypeId: u, userType1: u1, districtId } = role;
      const hasSubj = !!counList.SubjectId;
      const hasPanel = !!counList.PanelType;
      const hasBlock = !!counList.BlockId;

      const subjectDesig = SUBJECT_DESIGNATIONS.includes(desig);

      if (subjectDesig) {
        // Enabled (disabled=false) when any of these branches is satisfied.
        const enabled =
          (p === 'order-copy' && hasSubj) ||
          (loc !== '1' && hasSubj && hasPanel) ||
          (loc === '1' &&
            hasSubj &&
            hasPanel &&
            districtId === 16 &&
            u === '10' &&
            u1 === '2') ||
          (loc === '1' && hasSubj && hasPanel && hasBlock) ||
          (p !== '' &&
            u === '10' &&
            loc === '1' &&
            hasSubj &&
            hasPanel &&
            hasBlock) ||
          (u === '5' && hasSubj && hasPanel) ||
          (cType === '4' && hasSubj);
        return !enabled;
      }

      const enabled =
        (loc === '1' && hasPanel && hasBlock) ||
        (loc === '1' &&
          hasPanel &&
          districtId === 16 &&
          u === '10' &&
          u1 === '2') ||
        (loc !== '1' && hasPanel) ||
        p === 'order-copy' ||
        (loc === '1' &&
          hasPanel &&
          hasBlock &&
          p !== 'order-copy' &&
          p !== '') ||
        (loc === '1' && u === '5' && hasPanel) ||
        cType === '4';
      return !enabled;
    },
    [role, path]
  );

  const districtHeader =
    role.userTypeId === '5' || role.userTypeId === '9'
      ? 'District Name'
      : role.userTypeId === '6'
        ? 'Block Name'
        : role.userTypeId === '10'
          ? 'Edu District'
          : 'District Name';
  const districtValue =
    role.userTypeId === '9'
      ? s.districtName
      : role.userTypeId === '6'
        ? s.blockName
        : role.userTypeId === '10'
          ? s.edn_dist_name
          : role.userTypeId === '5'
            ? '-'
            : s.districtName;

  // Reusable right-aligned search header for the detail tables.
  const searchHeader = (value: string, onChange: (v: string) => void) => (
    <div className="teacher-search">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="text"
          placeholder="Search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
    </div>
  );

  // ── Selected-teacher table (shared by tabs 1 & 2) ──────────────────────────
  const selectedTeacherTable = path === '' && (
    <div className="mb-2">
      <h5 className="heading">Selected Teacher Details</h5>
      <div className="card Counselling-Secondary">
        <DataTable value={selectedTeacher} responsiveLayout="scroll">
          {columnSets.teacher.map((col) => (
            <Column key={col.field} field={col.field} header={col.header} />
          ))}
        </DataTable>
      </div>
    </div>
  );

  const vacanciesTable = (
    <DataTable
      value={vacancyList}
      paginator
      rows={10}
      rowsPerPageOptions={[10, 25, 50]}
      rowHover
      responsiveLayout="scroll"
      selectionMode="single"
      selection={selectedVacancyRow}
      onSelectionChange={(e) => setSelectedVacancyRow(e.value)}
      onRowSelect={onRowSelect}
      emptyMessage="No Records Found"
      globalFilter={vacancyFilter}
      header={searchHeader(vacancyFilter, setVacancyFilter)}
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
      paginatorRight={
        <b>Total Number of Vacancy : {totalVacncy ? totalVacncy : 0}</b>
      }
    >
      <Column header="#" body={(_r, opt) => opt.rowIndex + 1} />
      {columnSets.vacancies.map((col) => (
        <Column
          key={col.field}
          field={col.field}
          header={col.header}
          sortable
        />
      ))}
    </DataTable>
  );

  return (
    <div className="font-small main-body-content">
      {/* breadcrumb */}
      <div className="row">
        <div className="col-9">
          {/* <div className="breadcrumbs c-black span">
            <h6 className="url">
              <span
                className="flex-gap-right cursor-pointer"
                onClick={handleCandidateURL}
              >
                <b>{pageTitle}</b>
              </span>
              &nbsp;&nbsp;
              <i className="feather icon-chevron-right" />
              &nbsp;&nbsp;
              <span
                className="flex-gap-right cursor-pointer"
                onClick={handleCounsellingURL}
              >
                {isCounsellingCard ? 'Counselling List' : startEndRow?.Title}
              </span>
            </h6>
          </div> */}
        </div>
        {isCounsellingCard && (
          <div className="col-3 text-right text-end">
            <SearchBox
              key={searchResetKey}
              onSearch={onSearch}
              placeholder="Search.."
            />
          </div>
        )}
      </div>

      {/* ══════════════ COUNSELLING LIST ══════════════ */}
      {isCounsellingCard && (
        <>
          {isLoading && (
            <div className="p-card">
              <div className="row">
                <div className="col-12 text-center">
                  <ProgressSpinner
                    style={{ width: '40px', height: '40px' }}
                    strokeWidth="6"
                    animationDuration=".5s"
                  />
                </div>
              </div>
            </div>
          )}
          {!isLoading && counsellingList.length === 0 && (
            <div className="p-card">
              <div className="row">
                <div className="col-12 text-center">
                  <b>No records found.</b>
                </div>
              </div>
            </div>
          )}
          {!isLoading &&
            counsellingList.map((counList, i) => (
              <CounsellingCard
                key={`${counList.IndexId ?? 'row'}-${i}`}
                counList={counList}
                index={i}
                path={path}
                blockList={blockList}
                districtHeader={districtHeader}
                districtValue={districtValue}
                disabled={isStartDisabled(counList)}
                onPanelType={handlePanelType}
                onSubject={handleSubject}
                onBlock={handleBlock}
                onStart={handleStart}
                onReset={clickReset}
              />
            ))}
        </>
      )}

      {/* ══════════════ DETAIL SCREEN ══════════════ */}
      {!isCounsellingCard && (
        <div className="mt-3">
          {startEndRow?.schType !== '2' && (
            <b>
              அதிக எண்ணிக்கையால் காலிப்பணியிடங்கள் உள்ள மாவட்டத்திற்கு மட்டுமே
              துறை மாறுதல் / அலகு மாறுதல் கலந்தாய்வில் கான்பிக்கப்படும்.
            </b>
          )}
          <br />
          <br />
          <div className="row">
            <div className="col-8">
              <SelectButton
                value={tabKeyName}
                options={tabOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => e.value && handleTabClick(e.value)}
              />
            </div>
            <div className="col-4 text-right text-end">
              {activeTab === 0 && (
                <>
                  <Button
                    type="button"
                    label="Refresh Teacher List"
                    className="p-button-primary"
                    onClick={handleRefresh}
                  />
                  &nbsp;
                </>
              )}
              {(path === 'candidate' || path === 'vacancy') && (
                <>
                  <Button
                    type="button"
                    label="Export As Excel"
                    className="p-button-success"
                    disabled={
                      !(lists.teacherList.length > 0 || vacancyList.length > 0)
                    }
                    onClick={exportExcel}
                  />
                  &nbsp;
                </>
              )}
              <Button
                type="button"
                label="Back"
                className="p-button-danger"
                onClick={handleBack}
              />
            </div>
          </div>
          <br />

          {/* Tab 0: Teacher List */}
          {activeTab === 0 && (
            <div className="Counselling-Secondary">
              <DataTable
                value={lists.teacherList}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                rowHover
                responsiveLayout="scroll"
                emptyMessage="No Records Found"
                globalFilter={globalFilter}
                header={
                  <div className="teacher-search">
                    <span className="p-input-icon-left">
                      <i className="pi pi-search" />
                      <InputText
                        type="text"
                        placeholder="Search"
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                      />
                    </span>
                  </div>
                }
              >
                {columnSets.teacher.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                  />
                ))}
                {path === 'candidate' &&
                  str(startEndRow?.CounsellingType) !== '3' &&
                  str(startEndRow?.CounsellingType) !== '4' && (
                    <Column
                      header="Priority Document"
                      body={(row: any) =>
                        row.priorityDoc ? (
                          <Button
                            type="button"
                            className="p-button-primary mt-1"
                            label="View"
                            onClick={() => getPriorityFile(row.priorityDoc)}
                          />
                        ) : null
                      }
                    />
                  )}
                {path !== 'candidate' && (
                  <Column
                    header="Action"
                    body={(row: any, opt) =>
                      showRowActions(row, opt.rowIndex) ? (
                        <span>
                          {str(startEndRow?.CounsellingType) !== '3' &&
                            str(startEndRow?.willing_sts) === '1' && (
                              <>
                                <Button
                                  type="button"
                                  className="p-button-warning mt-1"
                                  label="Not Willing"
                                  onClick={() => clickNotWillingBtn(row)}
                                />
                                &nbsp;
                              </>
                            )}
                          {str(startEndRow?.CounsellingType) === '3' &&
                            str(startEndRow?.willing_sts) === '1' && (
                              <>
                                <Button
                                  type="button"
                                  className="p-button-warning mt-1"
                                  label="Relinguish"
                                  onClick={() => clickRelinguish(row)}
                                />
                                &nbsp;
                              </>
                            )}
                          <Button
                            type="button"
                            className="p-button-danger mt-1"
                            label="Absent"
                            onClick={() => clickAbsentBtn(row)}
                          />
                          &nbsp;
                          <Button
                            type="button"
                            className="p-button-primary mt-1"
                            label="Select"
                            onClick={() => clickSelectBtn(row)}
                          />
                        </span>
                      ) : null
                    }
                  />
                )}
                {path === 'candidate' && (
                  <Column
                    header="Status"
                    body={(row: any) =>
                      str(row.teacher_status) === '1'
                        ? 'Taken'
                        : str(row.teacher_status) === '2'
                          ? 'Absent'
                          : str(row.teacher_status) === '3'
                            ? 'Not Willing / Relinguish'
                            : 'Not Taken'
                    }
                  />
                )}
              </DataTable>
            </div>
          )}

          {/* Tab 1: Vacancies */}
          {activeTab === 1 && (
            <>
              {selectedTeacherTable}
              <div className="Counselling-Secondary">
                <TabView
                  activeIndex={activeIndexId}
                  onTabChange={(e) => handleTabView(e.index)}
                >
                  {/*
                    Build the panels array explicitly so TabView never receives
                    a falsy child (which breaks its internal index mapping and
                    leaves the tab un-highlighted on the vacancy path).
                  */}
                  {path === ''
                    ? [
                        <TabPanel
                          key="pre"
                          header="Pre Selected Vacancies List"
                        >
                          {vacanciesTable}
                        </TabPanel>,
                        <TabPanel key="all" header="Vacancies List">
                          {vacanciesTable}
                        </TabPanel>,
                      ]
                    : [
                        <TabPanel key="all" header="Vacancies List">
                          {vacanciesTable}
                        </TabPanel>,
                      ]}
                </TabView>
              </div>
            </>
          )}

          {/* Tab 2: Confirmation */}
          {activeTab === 2 && (
            <>
              {selectedTeacherTable}
              <br />
              <h5 className="heading">Confirmation List</h5>
              <div className="Counselling-Secondary">
                <DataTable
                  value={confirmationList}
                  paginator
                  rows={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  rowHover
                  responsiveLayout="scroll"
                  emptyMessage="No Records Found"
                  globalFilter={confirmationFilter}
                  header={searchHeader(
                    confirmationFilter,
                    setConfirmationFilter
                  )}
                >
                  <Column header="#" body={(_r, opt) => opt.rowIndex + 1} />
                  {columnSets.confirmation.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      sortable
                    />
                  ))}
                  <Column
                    header="Action"
                    body={(row: any) => (
                      <>
                        <Button
                          type="button"
                          className="p-button-success"
                          label="Confirm"
                          onClick={() => clickConfirmBtn(row)}
                        />
                        &nbsp;
                        <Button
                          type="button"
                          className="p-button-danger mt-2"
                          label="Reject"
                          onClick={clickRejectBtn}
                        />
                      </>
                    )}
                  />
                </DataTable>
              </div>
            </>
          )}

          {/* Tab 3: Not Willing / Relinguish */}
          {activeTab === 3 && (
            <div className="Counselling-Secondary">
              <DataTable
                value={lists.notWillingList}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                rowHover
                responsiveLayout="scroll"
                emptyMessage="No Records Found"
                globalFilter={notWillingFilter}
                header={searchHeader(notWillingFilter, setNotWillingFilter)}
              >
                {columnSets.notWilling.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                  />
                ))}
                {str(startEndRow?.CounsellingType) !== '3' && (
                  <Column
                    header="Action"
                    body={(row: any) =>
                      showWillingButton(row) ? (
                        <Button
                          type="button"
                          className="p-button-success"
                          label="Willing"
                          onClick={() => notWillRevert(row)}
                        />
                      ) : null
                    }
                  />
                )}
                {str(startEndRow?.CounsellingType) === '3' && (
                  <Column
                    header="Download Report"
                    body={(row: any, opt) => (
                      <Button
                        type="button"
                        className="p-button-danger"
                        icon={
                          row.pdfLoading
                            ? 'pi pi-spin pi-spinner'
                            : 'pi pi-file'
                        }
                        onClick={() => onRowPDF(row, '2', opt.rowIndex)}
                      />
                    )}
                  />
                )}
              </DataTable>
            </div>
          )}

          {/* Tab 4: Absent */}
          {activeTab === 4 && (
            <div className="Counselling-Secondary">
              <DataTable
                value={lists.absentList}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                rowHover
                responsiveLayout="scroll"
                emptyMessage="No Records Found"
                globalFilter={absentFilter}
                header={searchHeader(absentFilter, setAbsentFilter)}
              >
                {columnSets.absent.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                  />
                ))}
              </DataTable>
            </div>
          )}

          {/* Tab 5: Download Order */}
          {activeTab === 5 && (
            <div className="Counselling-Secondary">
              <DataTable
                value={lists.finalList}
                loading={loading}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                rowHover
                responsiveLayout="scroll"
                emptyMessage="No Records Found"
                globalFilter={finalFilter}
                header={searchHeader(finalFilter, setFinalFilter)}
              >
                <Column field="seniority_state" header="Seniority No." />
                <Column
                  header="Teacher / App Id"
                  body={(row: any) =>
                    str(startEndRow?.CounsellingType) === '4'
                      ? row.application_id
                      : row.teacher_id
                  }
                />
                <Column field="name" header="Name" />
                {str(startEndRow?.CounsellingType) !== '4' && (
                  <Column
                    header="Old Designation"
                    body={(row: any) => (
                      <span>
                        {row.Designation}
                        {SUBJECT_DESIGNATIONS.includes(str(row.desgnation)) && (
                          <> - {row.Subject}</>
                        )}
                      </span>
                    )}
                  />
                )}
                <Column
                  header="New Designation"
                  body={(row: any) => (
                    <span>
                      {row.NewDesignation}
                      {SUBJECT_DESIGNATIONS.includes(
                        str(row.new_designation)
                      ) && <> - {row.NewSubject}</>}
                    </span>
                  )}
                />
                {str(startEndRow?.CounsellingType) !== '4' && (
                  <Column
                    header="Old School Name"
                    body={(row: any) => (isOfc ? row.ofcNme : row.school_name)}
                  />
                )}
                <Column
                  header="New School Name"
                  body={(row: any) =>
                    isOfc ? row.newSchlNme2 : row.newSchlNme
                  }
                />
                <Column
                  header="Download Report"
                  className="text-center"
                  body={(row: any, opt) => {
                    const mode = showFinalPdf(row);
                    if (mode === 'dash') return <span>-</span>;
                    return (
                      <Button
                        type="button"
                        className="p-button-danger"
                        icon={
                          row.pdfLoading
                            ? 'pi pi-spin pi-spinner'
                            : 'pi pi-file'
                        }
                        onClick={() => onRowPDF(row, 1, opt.rowIndex)}
                      />
                    );
                  }}
                />
              </DataTable>
            </div>
          )}
        </div>
      )}

      {/* ══════════════ DIALOGS ══════════════ */}

      {/* Confirmation */}
      <Dialog
        header="Confirmation"
        visible={confirmModal}
        modal
        closable={false}
        draggable={false}
        onHide={handleCancel}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '70vw' }}
      >
        <br />
        <br />
        {!moveToTransfer && (
          <>
            {str(startEndRow?.CounsellingType) === '1' &&
              str(startEndRow?.schType) !== '2' && (
                <div className="row">
                  <div className="col-4">
                    <label>Vacancy / Need</label>
                    <Dropdown
                      value={vacNeed}
                      options={OPTION_LIST}
                      optionLabel="label"
                      optionValue="value"
                      disabled={isVacNeedDisabled}
                      placeholder="Select a vacancy / need"
                      showClear
                      onChange={(e) => setVacNeed(e.value)}
                    />
                  </div>
                </div>
              )}
            {((str(startEndRow?.CounsellingType) === '1' && vacNeed) ||
              str(startEndRow?.CounsellingType) !== '1') && (
              <div className="row">
                <div className="col vacancy-table">
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>School Name</th>
                        <th>Vacancy Before Transfer</th>
                        <th>Vacancy After Transfer</th>
                        {str(startEndRow?.CounsellingType) === '1' &&
                          str(startEndRow?.schType) !== '2' && (
                            <>
                              <th>Need Before Transfer</th>
                              <th>Need After Transfer</th>
                            </>
                          )}
                      </tr>
                    </thead>
                    <tbody>
                      {str(startEndRow?.CounsellingType) !== '1' &&
                        str(startEndRow?.CounsellingType) !== '4' && (
                          <tr>
                            <td>
                              {role.isScert
                                ? clickSelectRow?.ofcNme
                                : clickSelectRow?.school_name ||
                                  clickSelectRow?.ofcNme}{' '}
                              - {clickSelectRow?.udise_code}
                            </td>
                            <td>
                              {techVacDetails?.vac
                                ? num(techVacDetails.vac)
                                : 0}
                            </td>
                            <td>
                              {str(startEndRow?.CounsellingType) === '2'
                                ? str(confirmRow?.ResultVacYn) === '1'
                                  ? num(techVacDetails?.vac)
                                    ? num(techVacDetails.vac) + 1
                                    : 1
                                  : num(techVacDetails?.vac)
                                    ? num(techVacDetails.vac)
                                    : 0
                                : num(techVacDetails?.vac)
                                  ? num(techVacDetails.vac) + 1
                                  : 1}
                            </td>
                            {str(startEndRow?.CounsellingType) === '1' && (
                              <>
                                <td>
                                  {techVacDetails?.need
                                    ? num(techVacDetails.need)
                                    : 0}
                                </td>
                                <td>
                                  {techVacDetails?.need
                                    ? num(techVacDetails.need) + 1
                                    : 1}
                                </td>
                              </>
                            )}
                          </tr>
                        )}
                      <tr>
                        <td>
                          {rowSelect?.data?.school_name} -{' '}
                          {rowSelect?.data?.udise_code}
                        </td>
                        <td>
                          {rowSelect?.data?.vac ? rowSelect.data.vac : '-'}
                        </td>
                        <td>
                          {rowSelect?.data?.vac
                            ? str(startEndRow?.CounsellingType) === '1'
                              ? str(vacNeed) === '2'
                                ? num(rowSelect.data.vac) - 1
                                : num(rowSelect.data.vac)
                              : num(rowSelect.data.vac) - 1
                            : '-'}
                        </td>
                        {str(startEndRow?.CounsellingType) === '1' &&
                          str(startEndRow?.schType) !== '2' && (
                            <>
                              <td>{num(rowSelect?.data?.need)}</td>
                              <td>
                                {rowSelect?.data?.need && str(vacNeed) === '1'
                                  ? num(rowSelect.data.need) - 1
                                  : num(rowSelect?.data?.need)}
                              </td>
                            </>
                          )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="row mt-4">
              <div className="col">
                <label>Are you sure you want to confirm ?</label>
              </div>
            </div>
            {str(banStatus) === '0' && (
              <div className="row">
                <div className="col note">
                  Your counselling is stopped. So, Please contact your district
                  admin.
                </div>
              </div>
            )}
            <br />
            <div className="row">
              <div className="col-12 text-right text-end">
                <Button
                  type="button"
                  className="p-button-success"
                  disabled={handleConfirmLoading}
                  icon={handleConfirmLoading ? 'pi pi-spin pi-spinner' : ''}
                  label={!handleConfirmLoading ? 'Confirm' : ''}
                  onClick={handleConfirm}
                />
                &nbsp;
                {!handleConfirmLoading && (
                  <Button
                    type="button"
                    className="p-button-danger"
                    label="Cancel"
                    onClick={handleCancel}
                  />
                )}
              </div>
            </div>
          </>
        )}
        {moveToTransfer && (
          <div className="row">
            <div className="col text-center">
              <br />
              <b>
                <i className="pi pi-check-circle" />
              </b>
              <br />
              <br />
              <b className="text-style">
                {clickSelectRow?.name} Transferred Successfully!....
              </b>
              <br />
              <div className="col-12 text-right text-end mt-3">
                <Button
                  type="button"
                  className="p-button-success"
                  disabled={moveToTransferLoading}
                  icon={moveToTransferLoading ? 'pi pi-spin pi-spinner' : ''}
                  label={!moveToTransferLoading ? 'Go To Teacher List' : ''}
                  onClick={goToTeacherList}
                />
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Reject */}
      <Dialog
        header="Reject"
        visible={rejectModal}
        modal
        onHide={handleCancel}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '40vw' }}
      >
        <div className="row mt-4 pt-2">
          <div className="col">
            <label>Are you sure you want to reject ?</label>
          </div>
        </div>
        <div className="row mt-4 pt-2">
          <div className="col-12 text-right text-end">
            <Button
              type="button"
              className="p-button-success"
              label="Reject"
              onClick={handleReject}
            />
            &nbsp;
            <Button
              type="button"
              className="p-button-danger"
              label="Cancel"
              onClick={handleCancel}
            />
          </div>
        </div>
      </Dialog>

      {/* Not Willing / Absent / Willing / Relinguish confirm */}
      <Dialog
        header={notWillAbsText}
        visible={notWillAbsModal}
        modal
        onHide={handleCancel}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '40vw' }}
      >
        <div className="row mt-4">
          <div className="col">
            <label>{notWillAbsCont}</label>
          </div>
        </div>
        <div className="row mt-4">
          <div className="col-12 text-right text-end">
            <Button
              type="button"
              className="p-button-success"
              label="Yes"
              onClick={handleNotWillAbs}
            />
            &nbsp;
            <Button
              type="button"
              className="p-button-danger"
              label="No"
              onClick={handleCancel}
            />
          </div>
        </div>
      </Dialog>

      {/* Seniority Reset */}
      <Dialog
        header="Seniority Reset"
        visible={resetModel}
        modal
        onHide={handleCancel}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '70vw' }}
      >
        <div className="row mt-4">
          <div className="col">
            <label>
              Are you sure you want to reset seniority order for this
              counselling ?
            </label>
          </div>
        </div>
        <div className="row">
          <div className="col-12 text-right text-end">
            <Button
              type="button"
              className="p-button-success"
              label="Yes"
              onClick={handleReset}
            />
            &nbsp;
            <Button
              type="button"
              className="p-button-danger"
              label="No"
              onClick={handleCancel}
            />
          </div>
        </div>
      </Dialog>

      {/* Relinguish */}
      <Dialog
        header="Relinguish"
        visible={relinguishModal}
        modal
        onHide={() => setRelinguishModal(false)}
        draggable={false}
        appendTo="self"
        headerClassName="dialog-header-secondary"
        blockScroll
        resizable={false}
        style={{ width: '70vw' }}
      >
        <div className="row mt-4">
          <div className="col-3">
            <label>Relinguish</label>
            <Dropdown
              value={relinguish}
              options={RELINGUISH_LIST}
              optionLabel="label"
              optionValue="value"
              placeholder="Select a relinguish"
              showClear
              onChange={(e) => setRelinguish(e.value)}
            />
          </div>
        </div>
        <br />
        <div className="row">
          <div className="col-12 text-right text-end">
            <Button
              type="button"
              className="p-button-success"
              label="Submit"
              onClick={handleRelinguishSubmit}
            />
            &nbsp;
            <Button
              type="button"
              className="p-button-danger"
              label="Cancel"
              onClick={() => setRelinguishModal(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DEE;

/* eslint-enable @typescript-eslint/no-explicit-any */
